import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { useMemo, useRef, useState } from 'react'

import {
  useCreateConsultationReasonMutation,
  useDeleteConsultationReasonMutation,
  useGetConsultationReasonsQuery,
  useUpdateConsultationReasonMutation,
} from '@/core/services/consultation'

import { ConsultationReasonData, ConsultationReasonsHandle } from '../ui/ConsultationReasons'

export const DEFAULT_CONSULTATION_REASONS = [
  'Fever',
  'Cough',
  'Cold symptoms',
  'Sore throat',
  'Headache',
  'Abdominal pain',
  'Chest pain',
  'Fatigue',
  'Skin rash',
  'Allergic reaction',
  'Hypertension follow-up',
  'Diabetes follow-up',
  'Asthma / respiratory difficulty',
  'Ear pain',
  'Eye discomfort',
  'Urinary symptoms',
  'Joint or muscle pain',
  'Back pain',
  'Nausea or vomiting',
  'Diarrhea',
  'Dizziness',
  'Wound care',
  'Post-surgery follow-up',
  'Medication renewal',
  'General check-up',
  'Anxiety or mental health',
  'Pregnancy follow-up',
  'Pediatric consultation',
] as const

export function useConsultationReasons() {
  const { enqueueSnackbar } = useSnackbar()
  const consultationReasonsRef = useRef<ConsultationReasonsHandle>(null)
  const [isSeeding, setIsSeeding] = useState(false)

  // Fetch consultation reasons
  const { data: consultationReasons = [], isLoading: isListing } = useGetConsultationReasonsQuery({})

  // Transform data for UI
  const consultationReasonsData = useMemo(
    () =>
      consultationReasons.map((consultationReason) => ({
        id: consultationReason._id,
        name: consultationReason.name,
      })),
    [consultationReasons]
  )

  // Mutations
  const [createConsultationReason, { isLoading: isCreating }] = useCreateConsultationReasonMutation()
  const [updateConsultationReason, { isLoading: isUpdating }] = useUpdateConsultationReasonMutation()
  const [deleteConsultationReason, { isLoading: isDeleting }] = useDeleteConsultationReasonMutation()

  // Handlers
  const handleCreate = async (values: Partial<ConsultationReasonData>) => {
    if (values.name) {
      try {
        await createConsultationReason({ name: values.name }).unwrap()
        enqueueSnackbar(t`Consultation reason created successfully`, { variant: 'success' })
        consultationReasonsRef.current?.toggleOptionFormDialog()
      } catch (error) {
        enqueueSnackbar(t`Unable to create consultation reason`, { variant: 'error' })
      }
    }
  }

  const handleUpdate = async (consultationReasonId: string, values: Partial<ConsultationReasonData>) => {
    if (values.name) {
      try {
        await updateConsultationReason({
          id: consultationReasonId,
          name: values.name,
        }).unwrap()
        enqueueSnackbar(t`Consultation reason updated successfully`, { variant: 'success' })
        consultationReasonsRef.current?.toggleOptionFormDialog()
      } catch (error) {
        enqueueSnackbar(t`Unable to update consultation reason`, { variant: 'error' })
      }
    }
  }

  const handleDelete = async (consultationReasonId: string) => {
    try {
      await deleteConsultationReason(consultationReasonId).unwrap()
      enqueueSnackbar(t`Consultation reason deleted successfully`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t`Unable to delete consultation reason`, { variant: 'error' })
    }
  }

  const handleSeedDefaults = async () => {
    if (isSeeding) return
    setIsSeeding(true)
    const existingNames = new Set(consultationReasons.map((consultationReason) => consultationReason.name.trim().toLowerCase()))
    let addedCount = 0
    let skippedCount = 0
    try {
      for (const name of DEFAULT_CONSULTATION_REASONS) {
        if (existingNames.has(name.trim().toLowerCase())) {
          skippedCount += 1
          continue
        }
        await createConsultationReason({ name }).unwrap()
        addedCount += 1
      }
      enqueueSnackbar(t`${addedCount} reason(s) added, ${skippedCount} already present`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t`Unable to seed default consultation reasons`, { variant: 'error' })
    } finally {
      setIsSeeding(false)
    }
  }

  const handleSearch = (text: string) => {
    // TODO: Implement search functionality if needed
    // Currently the API doesn't support search params for consultation reasons
  }

  return {
    data: consultationReasonsData,
    isCreating,
    isDeleting,
    isListing,
    isSeeding,
    isUpdating,
    onCreate: handleCreate,
    onDelete: handleDelete,
    onSearch: handleSearch,
    onSeedDefaults: handleSeedDefaults,
    onUpdate: handleUpdate,
    ref: consultationReasonsRef,
  }
}
